import React from 'react';
import { useHasPermission } from '../../../../../v2BaseComponents/Permissions/hooks';
import useMedicalProfileCardsCards from '../../../../../../hooks/api/useMedicalProfileCards';
import { useProfilesTabNavigation } from '../../../../../../utils/appNavigationNames';
import { getIntl } from '../../../../../../utils/intl';
import urls from '../../../../../../utils/urls';
import { ActionButtonLayout } from '../../../../../v2BaseComponents/ActionButtonLayout';
import TitledSection from '../../../../../v2BaseComponents/TitledSection';
import MainContentContainer from '../../../../../v2BaseComponents/MainContentContainer';
import Stack, { ContentStack } from '../../../../../v2BaseComponents/Stack';
import Text from '../../../../../v2BaseComponents/Text';
import { AnimateLoadedContent } from '../../../../../v2BaseComponents/Loader';
import PressableDynamicOpacity from '../../../../../v2BaseComponents/PressableDynamicOpacity';
import { LoadableAvatar } from '../../../../../v2BaseComponents/Avatar';
import SizedView from '../../../../../v2BaseComponents/SizedView';
import { CARD_ASPECT_RATIO } from '../../../../../../utils/constants';

const intl = getIntl('MedicalProfileCards', {
    idCards: 'ID Cards',
    insuranceCards: 'Insurance Cards',
    addId: 'Add ID',
    addInsurance: 'Add Insurance',
    noIdCards: 'No ID cards have been provided.',
    noInsuranceCards: 'No insurance cards have been provided.',
});

export type MedicalProfileCardsOwnProps = {
    profileId: string;
};

export type MedicalProfileCardsProps = MedicalProfileCardsOwnProps;

const MedicalProfileCards: React.FC<MedicalProfileCardsProps> = ({ profileId }) => {
    const navigation = useProfilesTabNavigation();
    const canEdit = useHasPermission(profileId, 'idsAndInfo', 'edit');
    const { isLoading, cards } = useMedicalProfileCardsCards({ medicalProfileId: profileId });

    if (isLoading) return null;

    if (cards.idCards.length === 0 && cards.insuranceCards.length === 0 && !canEdit) return null;

    const renderSection = (
        title: string,
        cards: { description: string; id: string; url: string; onPress: () => void; date: string | null }[],
        noCardsMessage: string,
        cardType: 'id' | 'insurance',
    ) => (
        <TitledSection title={title}>
            {cards.length ? (
                <ContentStack>
                    {cards.map((card) => (
                        <PressableDynamicOpacity
                            key={card.url}
                            onPress={() => {
                                navigation.navigate('edit-card', {
                                    profileId,
                                    cardId: card.id,
                                    cardType,
                                    description: card.description,
                                });
                            }}
                            style={{ width: '100%' }}
                            testID={`edit-${cardType}-card-button`}
                        >
                            <SizedView placeholderStyle={{ width: '100%', height: 200 }}>
                                {({ width }) => {
                                    return (
                                        <Stack vertical key={card.url} space={0}>
                                            <LoadableAvatar
                                                avatarUrl={card.url}
                                                size={{
                                                    width,
                                                    height: width * (CARD_ASPECT_RATIO[1] / CARD_ASPECT_RATIO[0]),
                                                }}
                                                fallbackName=""
                                            />
                                            <Text variant="caption-1" alignText="center">
                                                {card.description}
                                            </Text>
                                        </Stack>
                                    );
                                }}
                            </SizedView>
                        </PressableDynamicOpacity>
                    ))}
                </ContentStack>
            ) : (
                <Text variant="card-support">{noCardsMessage}</Text>
            )}
        </TitledSection>
    );

    return (
        <AnimateLoadedContent isLoading={isLoading}>
            {() => (
                <ActionButtonLayout
                    whiteBackground
                    scrollable
                    actions={[
                        {
                            title: intl('addId'),
                            icon: 'PlusCircle',
                            onPress: () => {
                                navigation.navigate('add-card', {
                                    profileId,
                                    cardType: 'id',
                                });
                            },
                            show: canEdit,
                            testID: 'add-id-card-button',
                        },
                        {
                            title: intl('addInsurance'),
                            icon: 'PlusCircle',
                            onPress: () => {
                                navigation.navigate('add-card', {
                                    profileId,
                                    cardType: 'insurance',
                                });
                            },
                            show: canEdit,
                            testID: 'add-insurance-card-button',
                        },
                    ]}
                >
                    <MainContentContainer>
                        <ContentStack>
                            {renderSection(
                                intl('idCards'),
                                cards.idCards.map((card) => ({
                                    description: card.description,
                                    url: urls.api.medicalProfiles.cards.idCardImage(
                                        profileId,
                                        card.medicalProfileIdCardId,
                                    ),
                                    date: card.createdAt,
                                    id: card.medicalProfileIdCardId,
                                    onPress: () =>
                                        navigation.navigate('edit-card', {
                                            profileId,
                                            cardId: card.medicalProfileIdCardId,
                                            cardType: 'id',
                                            description: card.description,
                                        }),
                                })),
                                intl('noIdCards'),
                                'id',
                            )}
                            {renderSection(
                                intl('insuranceCards'),
                                cards.insuranceCards.map((card) => ({
                                    description: card.description,
                                    date: card.createdAt,
                                    url: urls.api.medicalProfiles.cards.insuranceCardImage(
                                        profileId,
                                        card.medicalProfileInsuranceCardId,
                                    ),
                                    id: card.medicalProfileInsuranceCardId,
                                    onPress: () =>
                                        navigation.navigate('edit-card', {
                                            profileId,
                                            cardId: card.medicalProfileInsuranceCardId,
                                            cardType: 'insurance',
                                            description: card.description,
                                        }),
                                })),
                                intl('noInsuranceCards'),
                                'insurance',
                            )}
                        </ContentStack>
                    </MainContentContainer>
                </ActionButtonLayout>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(MedicalProfileCards);
